import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { getApiErrorMessage } from '../utils/apiError';
import '../App.css';

const PASSWORD_REQUIREMENTS = 'At least 8 characters, one letter, one number, no spaces.';

function validatePassword(pw: string): string | null {
  if (pw.length < 8) return 'Password must be at least 8 characters';
  if (/\s/.test(pw)) return 'Password cannot contain spaces';
  if (!/[a-zA-Z]/.test(pw)) return 'Password must contain at least one letter';
  if (!/\d/.test(pw)) return 'Password must contain at least one number';
  return null;
}

const InviteAccept: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthenticated } = useAuthStore();

  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);

  // Beta: keep route active for invited users, but avoid exposing a usable "signup page" to the public.
  // Without a token in the URL, redirect to the homepage.
  useEffect(() => {
    if (!token) {
      navigate('/', { replace: true });
    }
  }, [token, navigate]);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Missing invite token. Please use the link from your email.');
      return;
    }
    if (!fullName.trim()) {
      setError('Full name is required.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const pwErr = validatePassword(password);
    if (pwErr) {
      setError(pwErr);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/invite/accept', {
        token,
        full_name: fullName.trim(),
        password,
      });
      const { access_token } = res.data || {};
      if (!access_token) {
        throw new Error('No access token returned');
      }
      setAuthenticated(access_token);
      navigate('/dashboard');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to accept invite'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <div className="card" style={{ width: '420px' }}>
        <h2>Complete your beta invite</h2>
        <p style={{ marginTop: '8px', color: '#555' }}>
          Set a password to finish creating your account.
        </p>

        {error && <div style={{ color: 'red', margin: '15px 0' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Full Name</label>
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
              {PASSWORD_REQUIREMENTS}
            </div>
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Loading...' : 'Accept Invite'}
          </button>
        </form>

        <p style={{ marginTop: '15px', fontSize: '13px', color: '#666' }}>
          If you already have an account, you can <a href="/login">login</a>.
        </p>
      </div>
    </div>
  );
};

export default InviteAccept;

